import { FocusEvent, ReactNode } from "react";
import { Dialog, SwipeableDrawer, useMediaQuery, useTheme } from "@mui/material";

import { modalTransitionProps, sheetTransitionProps } from "./ScaleTransition";

// Responsive shell for the settlement / challenge popups (SettlementPanel's own challenge
// dialog, AnchorDeedDialog, both Judge*Dialogs, ChallengeTermDialog and the three
// components/Challenge dialogs). It is only the container — everything inside stays exactly
// as each dialog writes it.
//
// Desktop (>= sm, 560px here) is unchanged: the centred white card these popups have always
// used — 18px radius, 24px padding, capped at `maxWidth`.
//
// Below that it becomes the bottom sheet every other popup in the app uses on mobile — the
// bid/confirm popups in EFTActionButton, TreeClaimModalNew, the offer modals, ComplaintModal:
// a bottom-anchored SwipeableDrawer with rounded top corners, the blurred backdrop, and its
// own scroll. The drawer opens only from code (`disableSwipeToOpen` + `swipeAreaWidth={0}`),
// matching those; swiping the sheet down still closes it.
//
// Two reasons the phone lane is a sheet rather than the same dialog with tighter margins:
//
//   These popups are tall. AnchorDeedDialog is three numbered steps, a download, a file
//     picker and a date field; the judge dialogs carry a quoted challenge plus a warning
//     panel. A centred dialog taller than the viewport clips at both ends, while a sheet
//     scrolls from a fixed bottom edge.
//   The actions sit at the bottom of every one of them. Pinned to the bottom of the screen
//     they land under the thumb instead of halfway up it.
//
// Motion: the desktop card opens as a transitions-dev "Modal" (grows from 0.96 over 250ms, dips
// back over 150ms) instead of MUI's plain Fade, and the sheet slides as a panel (400ms in on the
// project entrance curve, 350ms out) instead of MUI's 225/195ms defaults. See ScaleTransition.
//
// The desktop paper also gets a `min(...)` cap: the sx below overrides MUI's default
// `maxWidth: calc(100% - 64px)`, so without it a 520px card would overflow the 560-568px
// sliver just above the breakpoint.

// A bottom sheet sits where the on-screen keyboard opens, so a field near the foot of a tall
// popup (the challenge reason, the evidence box, the deed date) ends up behind it. Nudge the
// focused field into the middle of the sheet's own scroll once the keyboard has settled.
// Desktop never runs this — the dialog is centred and the keyboard is not in the way.
const keepFocusedFieldVisible = (event: FocusEvent<HTMLDivElement>) => {
  const field = event.target as HTMLElement;
  if (!field.matches?.("input, textarea, select")) {
    return;
  }
  const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  // The delay is the keyboard animation — scrolling before it lands measures the old viewport.
  window.setTimeout(() => {
    field.scrollIntoView({ block: "center", behavior: reducedMotion ? "auto" : "smooth" });
  }, 300);
};

export interface PopupDialogProps {
  open: boolean;
  onClose: () => void;
  // Desktop card width. The mobile sheet is always full-bleed, so this is desktop-only.
  maxWidth?: number;
  // "card" is the original white paper with its own 24px padding. "sheet" is the V9 Settlement
  // redesign, where the whole popup *is* the grey surface: the paper paints #f2f2f2 and supplies
  // no padding, because the content (see settlementStyles' DialogSheet) owns the inset. Without
  // this the grey sheet renders as a panel inside a white frame, which the design doesn't have.
  // "plain" goes one further and paints nothing at all — no background, no padding, no shadow —
  // for content that is already a self-contained rounded card (the V9 offer/bid popups, whose
  // land image and purple panel carry their own 20px radius). On a white or grey paper those
  // corners would sit on a visible rectangle.
  surface?: "card" | "sheet" | "plain";
  children: ReactNode;
}

const SHEET_BG = "#f2f2f2";

const PopupDialog = ({ open, onClose, maxWidth = 520, surface = "card", children }: PopupDialogProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const sheet = surface === "sheet";
  const plain = surface === "plain";

  if (isMobile) {
    return (
      <SwipeableDrawer
        anchor="bottom"
        open={open}
        onClose={onClose}
        // Required by SwipeableDrawer, but unreachable: the sheet is opened from code only.
        onOpen={() => undefined}
        disableSwipeToOpen
        swipeAreaWidth={0}
        {...sheetTransitionProps}
        // A Drawer's modal root sits at `zIndex.drawer` (1200), and this app's AppBar
        // deliberately sits one above it (Header/index.tsx, `zIndex.drawer + 1`) — which on the
        // land page is a *sticky* header, so it paints over the top of the sheet and eats the
        // title. Lift the whole thing to `zIndex.modal` (1300), where the desktop Dialog already
        // is, so the sheet and its backdrop cover the header like any other modal.
        sx={{ zIndex: (theme) => theme.zIndex.modal }}
        slotProps={{
          backdrop: {
            sx: {
              backdropFilter: "blur(8px)",
              backgroundColor: "rgba(0, 0, 0, 0.5)",
            },
          },
        }}
        PaperProps={{
          sx: {
            backgroundColor: plain ? "transparent" : sheet ? SHEET_BG : "white",
            borderRadius: "20px 20px 0 0",
            // Must come after the line above, or it loses. A transparent paper has no corners
            // of its own to round — the content inside carries them.
            ...(plain && { boxShadow: "none", backgroundImage: "none", borderRadius: 0 }),
            // dvh, not vh: the mobile browser chrome is part of the viewport here, and vh
            // would push the actions under it.
            maxHeight: "90dvh",
            overflowY: "auto",
            WebkitOverflowScrolling: "touch",
            // Bottom padding clears the home indicator on notched devices. The sheet surface
            // keeps only that part — its content supplies the rest of the inset.
            padding: plain
              // A "plain" popup is a self-contained rounded card (or a stack of them), so it
              // keeps side and foot margins on a phone rather than bleeding to the screen
              // edges — otherwise its corners are cut off and it stops reading as a card.
              ? "0 16px calc(16px + env(safe-area-inset-bottom))"
              : sheet
              ? "0 0 env(safe-area-inset-bottom)"
              : "22px 18px calc(22px + env(safe-area-inset-bottom))",
          },
        }}
      >
        <div onFocus={keepFocusedFieldVisible}>{children}</div>
      </SwipeableDrawer>
    );
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      {...modalTransitionProps}
      PaperProps={{
        sx: {
          borderRadius: sheet || plain ? "20px" : "18px",
          // The theme puts a `1px solid #dee2e6` hairline on every MuiDialog paper
          // (theme.ts, MuiDialog.styleOverrides.paper). MuiDrawer has no such rule, so the
          // mobile lane above never draws it and the two lanes disagree. On a "plain" paper it
          // is worse than inconsistent: the paper is transparent, so the hairline is a rounded
          // rectangle floating around the hero and the panel, cutting across the seam the two
          // modules are meant to show. Drop it here for every surface; the theme rule still
          // stands for Dialogs that don't go through this shell.
          border: "none",
          p: sheet || plain ? 0 : "24px",
          ...(sheet && { backgroundColor: SHEET_BG }),
          // The plain paper is only a positioning frame; the content paints itself.
          ...(plain && { backgroundColor: "transparent", backgroundImage: "none", boxShadow: "none" }),
          width: "100%",
          maxWidth: `min(${maxWidth}px, calc(100% - 48px))`,
        },
      }}
    >
      {children}
    </Dialog>
  );
};

export default PopupDialog;
